// Guided walkthrough: a tutor marking up a real exam item, step by step.
// The passage (or transcript) is the whiteboard; each step draws a coloured
// highlight on the exact words and the tutor "speaks" the reasoning. This is
// how strategy is taught to a student who is alone with the screen.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Headphones, MessageSquare, Pause, PenLine, Play, RotateCcw } from 'lucide-react';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { langDir } from '@/lib/lang';
import { Button } from '@/components/ui/button';
import DiagramCard from '@/components/DiagramCard';
import ContentImage from '@/components/ContentImage';
import { cn } from '@/lib/utils';

// The recording the student would hear on test day. In the walkthrough it is
// replayable (unlike the exam, which plays once) so they can study the audio
// against the transcript. Binds to stimulus.audio_ref; when no audio has been
// rendered yet the control sits inert rather than vanishing, so the screen
// always reads as a listening task.
function WalkthroughAudio({ audioRef }) {
  const elRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    setUrl(null); setPlaying(false); setPct(0);
    if (!audioRef) return undefined;
    let alive = true;
    let objectUrl = null;
    api.contentAudioBlob(audioRef)
      .then((res) => { if (!alive) return; objectUrl = URL.createObjectURL(res.data); setUrl(objectUrl); })
      .catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [audioRef]);

  const toggle = () => {
    const el = elRef.current;
    if (!el) return;
    if (el.paused) el.play(); else el.pause();
  };
  const available = !!url;

  return (
    <div className="mb-4 flex items-center gap-3 border border-slate-200 bg-white px-4 py-3" data-testid="wt-audio">
      <button
        type="button"
        onClick={toggle}
        disabled={!available}
        data-testid="wt-audio-play"
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition-colors',
          available ? 'bg-indigo-600 hover:bg-indigo-700' : 'cursor-default bg-slate-300')}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-px" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <Headphones className="h-3.5 w-3.5" /> The recording
        </p>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {url && (
        <audio
          ref={elRef}
          src={url}
          data-testid="wt-audio-el"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setPct(0); }}
          onTimeUpdate={(e) => { const d = e.target.duration || 0; setPct(d ? (e.target.currentTime / d) * 100 : 0); }}
        />
      )}
    </div>
  );
}

// A fill-in-the-blank gap, drawn as a real blank box carrying its number. The
// gap the current step is teaching is lit; the rest sit quiet for context, the
// way a tutor would point at one blank while the others wait.
function Blank({ n, active }) {
  return (
    <span
      data-testid={`wt-blank-${n}`}
      className={cn(
        'mx-0.5 inline-flex min-w-[2.25rem] items-center justify-center rounded border-b-2 px-1.5 align-baseline text-[12px] font-semibold leading-none',
        active ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-300 bg-slate-100 text-slate-400'
      )}
    >
      {n}
    </span>
  );
}

// Split a run of passage text on {{N}} gap tokens, rendering each as a Blank.
// Operates on slices of the RAW passage so the highlight indices (computed
// against the raw text, gaps included) stay valid. Plain string when no gaps.
function withGaps(str, activeGap, keyPrefix) {
  if (!str.includes('{{')) return str;
  const out = [];
  const re = /\{\{(\d+)\}\}/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) out.push(str.slice(last, m.index));
    const n = Number(m[1]);
    out.push(<Blank key={`${keyPrefix}-g${i}`} n={n} active={n === activeGap} />);
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < str.length) out.push(str.slice(last));
  return out;
}

// Render text with cumulative highlights. `marks` = [{text, kind, live}] in
// reveal order; the live one (current step) animates the marker sweep. {{N}}
// gap tokens render as blanks, with the active gap lit.
function MarkedText({ text, marks, liveRef, activeGap }) {
  const ranges = useMemo(() => {
    const out = [];
    const used = [];
    marks.forEach((m, mi) => {
      if (!m.text) return;
      const at = text.indexOf(m.text);
      if (at < 0) return;
      // skip if this span overlaps one already placed
      if (used.some(([s, e]) => at < e && at + m.text.length > s)) return;
      used.push([at, at + m.text.length]);
      out.push({ start: at, end: at + m.text.length, kind: m.kind, live: m.live, key: mi });
    });
    return out.sort((a, b) => a.start - b.start);
  }, [text, marks]);

  if (ranges.length === 0) return <>{withGaps(text, activeGap, 't')}</>;

  const nodes = [];
  let cursor = 0;
  ranges.forEach((r) => {
    if (r.start > cursor) nodes.push(<span key={`t${cursor}`}>{withGaps(text.slice(cursor, r.start), activeGap, `t${cursor}`)}</span>);
    nodes.push(
      <mark
        key={`m${r.key}`}
        ref={r.live ? liveRef : null}
        className={cn('wt-mark', `wt-${r.kind}`, r.live && 'wt-live')}
      >
        {withGaps(text.slice(r.start, r.end), activeGap, `m${r.key}`)}
      </mark>
    );
    cursor = r.end;
  });
  if (cursor < text.length) nodes.push(<span key={`t${cursor}`}>{withGaps(text.slice(cursor), activeGap, `t${cursor}`)}</span>);
  return <>{nodes}</>;
}

export default function WalkthroughPlayer() {
  const { exam, walkthroughId } = useParams();
  const { lang } = useApp();
  const navigate = useNavigate();
  const [wt, setWt] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const liveRef = useRef(null);
  const scrollBoxRef = useRef(null);
  // Reserve a fixed height for the caption equal to the tallest caption in this
  // walkthrough, so the sticky header never grows/shrinks step to step and the
  // page below it stops bouncing up and down on Next.
  const capMeasureRef = useRef(null);
  const [capMinH, setCapMinH] = useState(0);

  useEffect(() => {
    api.walkthrough(exam, walkthroughId, lang)
      .then((res) => setWt(res.data.walkthrough))
      .catch((err) => setError(err.response?.data?.detail || 'Walkthrough not found.'));
  }, [exam, walkthroughId, lang]);

  // Center the live highlight WITHOUT moving the page: when the transcript has
  // its own scroll (desktop two-column), scroll inside that box so the question
  // beside it never leaves the screen. Fall back to page scroll when stacked.
  useEffect(() => {
    const mark = liveRef.current;
    if (!mark) return;
    const box = scrollBoxRef.current;
    if (box && box.scrollHeight > box.clientHeight + 4) {
      const bRect = box.getBoundingClientRect();
      const mRect = mark.getBoundingClientRect();
      const delta = (mRect.top - bRect.top) - box.clientHeight / 2 + mRect.height / 2;
      box.scrollTo({ top: box.scrollTop + delta, behavior: 'smooth' });
    } else {
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [step]);

  // Measure every caption (rendered invisibly at the real width) and reserve the
  // tallest. Re-runs when the walkthrough loads and on resize (wrapping changes).
  useLayoutEffect(() => {
    const node = capMeasureRef.current;
    if (!node) { setCapMinH(0); return; }
    const measure = () => {
      let max = 0;
      for (const child of node.children) max = Math.max(max, child.offsetHeight);
      setCapMinH(max);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [wt]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50">
        <p className="text-slate-500">{error}</p>
        <Link to={`/${exam}/lessons`} className="cta">Back to lessons</Link>
      </div>
    );
  }
  if (!wt) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400">Loading…</div>;

  const steps = wt.steps || [];
  const cur = steps[step] || {};
  const text = wt.stimulus?.text || wt.stimulus?.transcript || '';
  const isModel = wt.stimulus?.mode === 'model_answer';
  const isAudio = !isModel && !!wt.stimulus?.transcript && !wt.stimulus?.text;
  const scenario = wt.stimulus?.scenario;

  // marks revealed up to and including the current step
  const marks = steps.slice(0, step + 1)
    .map((s, i) => ({ text: s.highlight, kind: s.kind || 'clue', live: i === step }))
    .filter((m) => m.text);

  // Which blank this walkthrough is teaching, e.g. question.prompt "Gap 1" -> 1.
  const gapMatch = /(\d+)/.exec(wt.question?.prompt || '');
  const activeGap = gapMatch ? Number(gapMatch[1]) : null;

  const last = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="walkthrough-player">
      {/* tutor caption band, the voice, always in view */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to={`/${exam}/lessons`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
              <ArrowLeft className="h-4 w-4" /> Lessons
            </Link>
            <span className="text-xs font-medium tabular-nums text-slate-400" data-testid="wt-step">Step {step + 1} of {steps.length}</span>
          </div>
          <div className="relative mt-3">
            <p className="text-[17px] font-medium leading-7 text-slate-900" data-testid="wt-caption" dir={langDir(lang)} style={{ minHeight: capMinH || undefined }}>{cur.caption}</p>
            {/* invisible: holds the tallest caption's height so the layout below never shifts */}
            <div ref={capMeasureRef} aria-hidden className="pointer-events-none invisible absolute inset-0" dir={langDir(lang)}>
              {steps.map((s, i) => (
                <p key={i} className="text-[17px] font-medium leading-7">{s.caption}</p>
              ))}
            </div>
          </div>
          <div className="mt-3 flex gap-1">
            {steps.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Step ${i + 1}`}
                onClick={() => setStep(i)}
                className={cn('h-1 flex-1 rounded-full transition-colors', i <= step ? 'bg-indigo-600' : 'bg-slate-200')}
              />
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-6">
        <p className="mono-stat" dir={langDir(lang)}>{wt.title}</p>
        {wt.subtitle && <p className="mt-1 mb-6 text-sm text-slate-500" dir={langDir(lang)}>{wt.subtitle}</p>}

        {/* Task instructions (speaking model-answer walkthroughs). The picture
            itself is rendered inside the two-column grid below so it lives in
            the empty right column and stays visible while the student scrolls
            through the model answer. The scene text (between "The scene:" and
            the timing sentence) is intentionally NOT rendered — the picture
            is the scene; describing it in words to a student who can see it
            would be redundant and, historically, an eyesore. */}
        {isModel && scenario && (() => {
          const m = /^(.+?)\s*The scene:\s*(.+?)(\s+You have\s+.+)?$/is.exec(scenario);
          const instructions = m ? m[1].trim() : scenario;
          const timing = m && m[3] ? m[3].trim() : null;
          return (
            <div className="mb-4 border border-slate-200 bg-white p-5" data-testid="wt-scenario">
              <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <MessageSquare className="h-3.5 w-3.5" /> The task
              </p>
              <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-slate-700">{instructions}</p>
              {timing && <p className="mt-2 text-xs text-slate-500">{timing}</p>}
            </div>
          );
        })()}

        {/* Two columns so the marked line and the option it explains sit side by
            side. Passage + question + audio player + controls all stay LTR
            because that is exam content (English) — only the translated tutor
            caption above and the walkthrough title/subtitle flip for RTL langs.
            Uses `grid grid-cols-1 gap-6 lg:grid-cols-2` so order-* utilities
            work at every breakpoint — needed so a picture-scene walkthrough
            can render the picture ABOVE the model answer on mobile while
            sitting to its right on desktop. */}
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        {/* the whiteboard: the real item (or a model answer), marked up. The
            recording sits above it so it stays put while the transcript scrolls. */}
        <div className={cn(isModel && wt.stimulus?.image_ref && 'order-2 lg:order-none')}>
        {isAudio && <WalkthroughAudio audioRef={wt.stimulus?.audio_ref} />}
        {wt.stimulus?.diagram && (
          <div className="mb-4" data-testid="wt-diagram">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">What the screen shows</p>
            <DiagramCard diagram={wt.stimulus.diagram} />
          </div>
        )}
        <div ref={scrollBoxRef} className={cn('border bg-white p-7 shadow-sm lg:max-h-[calc(100vh-360px)] lg:overflow-y-auto', isModel ? 'border-emerald-200' : 'border-slate-200')}>
          {(isAudio || isModel) && (
            <p className={cn('mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide',
              isModel ? 'text-emerald-600' : 'text-slate-400')}>
              {isModel
                ? <><PenLine className="h-3.5 w-3.5" /> A model answer, built piece by piece</>
                : <>What the audio said</>}
            </p>
          )}
          <p className="whitespace-pre-line text-[15.5px] leading-[2] text-slate-800" data-testid="wt-passage">
            <MarkedText text={text} marks={marks} liveRef={liveRef} activeGap={activeGap} />
          </p>
        </div>
        </div>

        {/* AI-generated illustration for scene / prediction speaking tasks.
            Deliberately unlabeled — we do NOT claim this is a real CELPIP
            test picture (that would misrepresent copyrighted exam material)
            and any framing like "The picture on test day" would imply we're
            reproducing test content. The image sits inside a bordered card
            next to the model answer, which is enough visual context for the
            student to understand it's the picture the model answer describes.
            Sticky on desktop, order-1 on mobile so it appears before the
            model answer text. If image_ref is missing the card doesn't
            render — no text stand-in, ever. */}
        {isModel && wt.stimulus?.image_ref && (
          <div className="order-1 lg:order-none lg:sticky lg:top-[150px] border border-indigo-200 bg-indigo-50/40 p-3" data-testid="wt-picture">
            <ContentImage imageRef={wt.stimulus.image_ref} caption={wt.title || 'Illustration'} />
          </div>
        )}

        {/* the question, with the focused option lit. Handles single-answer
            (answer_index), multi-answer (answer_indices), and no-option types
            like Write from Dictation (answer_text revealed at the last step). */}
        {wt.question && (() => {
          const q = wt.question;
          const answerSet = new Set(q.answer_indices || (q.answer_index != null ? [q.answer_index] : []));
          const revealAnswer = step >= (wt.reveal_answer_step ?? steps.length - 1);
          return (
            <div className={cn('mt-5 lg:mt-0 lg:sticky lg:top-[150px] border bg-white p-6 transition-colors', cur.focus_question ? 'border-indigo-400 ring-1 ring-indigo-100' : 'border-slate-200')}>
              <p className="text-[15px] font-semibold text-slate-900">{q.prompt}</p>
              {q.options ? (
                <div className="mt-4 space-y-2">
                  {q.options.map((opt, oi) => {
                    const isAnswer = answerSet.has(oi);
                    const focused = cur.focus_option === oi;
                    return (
                      <div
                        key={oi}
                        data-testid={`wt-option-${oi}`}
                        className={cn(
                          'flex items-start gap-3 border px-4 py-2.5 text-[14px] leading-6 transition-all',
                          focused && !isAnswer && 'border-rose-400 bg-rose-50',
                          revealAnswer && isAnswer && 'border-emerald-500 bg-emerald-50',
                          !focused && !(revealAnswer && isAnswer) && 'border-slate-200 text-slate-600'
                        )}
                      >
                        <span className="mt-0.5 text-xs font-semibold text-slate-400">{String.fromCharCode(65 + oi)}</span>
                        <span className={cn(revealAnswer && isAnswer && 'font-medium text-emerald-950', focused && !isAnswer && 'text-rose-950')}>{opt}</span>
                      </div>
                    );
                  })}
                </div>
              ) : q.answer_text ? (
                <div className={cn('mt-4 border px-4 py-3 text-[14px] leading-6 transition-all', revealAnswer ? 'border-emerald-500 bg-emerald-50 font-medium text-emerald-950' : 'border-slate-200 text-slate-400')} data-testid="wt-answer-text">
                  {revealAnswer ? q.answer_text : 'Type exactly what you hear.'}
                </div>
              ) : null}
            </div>
          );
        })()}
        </div>

        {/* controls */}
        <div className="mt-8 flex items-center justify-between" data-testid="wt-controls">
          <button
            type="button"
            data-testid="wt-back"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900 disabled:opacity-30"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {last ? (
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setStep(0)} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
                <RotateCcw className="h-3.5 w-3.5" /> Replay
              </button>
              {wt.task_type && (
                <Button className="cta" data-testid="wt-practice" onClick={() => navigate(`/${exam}/sets/${wt.task_type}`)}>
                  Now try it yourself <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <Button className="cta" data-testid="wt-next" onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}>
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
